import { useState, useMemo, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import axios from "axios";
import {
  Camera, MapPin, TrendingUp, Calendar, Tag, Mail, Lock, User, Building2, Check, X, Sparkles,
} from "lucide-react";
import AdminPage from "./AdminPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_camera-site-profit/artifacts/zi4nnloe_White%20logo%20-%20no%20background.png";

const fmt = (n) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
  }).format(n);

/* ── Benefit bullets ─────────────────────────────────────────────────────── */
const BENEFITS = [
  "Predict your recurring revenue instantly",
  "Show dealers their earning potential",
  "Turn every deployed camera into monthly income",
];

/* ── Left text block ─────────────────────────────────────────────────────── */
function RevenueTextBlock() {
  return (
    <div className="rev-text-block">
      <div className="hero-tag">Dealer ROI Calculator</div>
      <h1 className="rev-h1">
        Project Your<br />
        <span className="hero-accent">Recurring Monthly</span><br />
        Revenue
      </h1>
      <p className="rev-sub">
        Calculate the true ROI of adding 247 Alert remote video monitoring to your
        security offerings. Every camera you deploy becomes predictable monthly income.
      </p>
      <ul className="benefit-list" data-testid="benefit-list">
        {BENEFITS.map((b) => (
          <li key={b} className="benefit-item">
            <span className="benefit-icon"><Check size={12} strokeWidth={3} /></span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <div className="rev-note">
        <Sparkles size={13} />
        <span>Enter your email to unlock your full revenue projection.</span>
      </div>
    </div>
  );
}

/* ── Revenue summary (the results area) ──────────────────────────────────── */
function RevenueSummary({ monthlyRMR, annualRMR, unlocked, onUnlock }) {
  return (
    <div className={`rev-results ${!unlocked ? "rev-results-locked" : ""}`}>
      {!unlocked && (
        <button
          type="button"
          className="rev-lock-overlay"
          onClick={onUnlock}
          data-testid="outputs-lock-overlay"
          aria-label="Click to unlock your results"
        >
          <div className="rev-lock-content">
            <Lock size={28} />
            <p className="rev-lock-cta">Click to unlock your results</p>
            <p className="rev-lock-sub">Enter your email to reveal RMR projections</p>
          </div>
        </button>
      )}

      <div className="rev-rmr">
        <div className="rev-rmr-head">
          <TrendingUp size={14} />
          <span>Monthly RMR</span>
        </div>
        <div className="rev-rmr-value" data-testid="rmr-output">{fmt(monthlyRMR)}</div>
        <div className="rev-rmr-sub">Recurring Monthly Revenue — Remote Video Monitoring</div>
      </div>

      <div className="rev-projections">
        <div className="rev-proj">
          <span className="rev-proj-label">Annual</span>
          <span className="rev-proj-value" data-testid="annual-output">{fmt(annualRMR)}</span>
        </div>
        <div className="rev-proj">
          <span className="rev-proj-label">3-Year</span>
          <span className="rev-proj-value" data-testid="three-year-output">{fmt(annualRMR * 3)}</span>
        </div>
        <div className="rev-proj">
          <span className="rev-proj-label">5-Year</span>
          <span className="rev-proj-value" data-testid="five-year-output">{fmt(annualRMR * 5)}</span>
        </div>
      </div>
    </div>
  );
}

/* ── Calculator card ─────────────────────────────────────────────────────── */
function RevenueCalculatorCard({
  sites, setSites, cameras, setCameras, sellPriceInput, handleSellPriceChange,
  sellPrice, totalCameras, monthlyRMR, annualRMR, unlocked, onUnlock,
}) {
  return (
    <div className="rev-card" data-testid="calculator-card">
      <div className="rev-card-header">
        <span className="rev-card-label">Configuration</span>
        <span className="rev-card-total" data-testid="total-cameras">
          {totalCameras} <span>cams</span>
        </span>
      </div>

      <div className="rev-inputs">
        {/* Sell Price */}
        <div className="rev-input-group rev-input-group-wide">
          <div className="rev-input-header">
            <Tag size={13} />
            <span>Your Sell Price</span>
          </div>
          <div className="rev-price-wrap">
            <span className="rev-price-dollar">$</span>
            <input
              data-testid="sell-price-input"
              type="text"
              inputMode="numeric"
              value={sellPriceInput}
              onChange={handleSellPriceChange}
              placeholder="110"
              className="rev-price-input"
            />
            <span className="rev-price-unit">/ cam / mo</span>
          </div>
        </div>

        {/* Sites Slider */}
        <div className="rev-input-group">
          <div className="rev-input-header">
            <MapPin size={13} />
            <span>Sites</span>
            <span className="rev-input-pill" data-testid="sites-value">{sites}</span>
          </div>
          <Slider
            data-testid="sites-slider"
            min={0}
            max={25}
            step={1}
            value={[sites]}
            onValueChange={(v) => setSites(v[0])}
            className="custom-slider"
          />
          <div className="rev-slider-range"><span>0</span><span>25</span></div>
        </div>

        {/* Cameras Slider */}
        <div className="rev-input-group">
          <div className="rev-input-header">
            <Camera size={13} />
            <span>Cameras / Site</span>
            <span className="rev-input-pill" data-testid="cameras-value">{cameras}</span>
          </div>
          <Slider
            data-testid="cameras-slider"
            min={1}
            max={24}
            step={1}
            value={[cameras]}
            onValueChange={(v) => setCameras(v[0])}
            className="custom-slider"
          />
          <div className="rev-slider-range"><span>1</span><span>24</span></div>
        </div>
      </div>

      <div className="rev-divider" />

      <RevenueSummary
        monthlyRMR={monthlyRMR}
        annualRMR={annualRMR}
        unlocked={unlocked}
        onUnlock={onUnlock}
      />
    </div>
  );
}

/* ── Full-screen email gate ──────────────────────────────────────────────── */
function FullScreenLeadGateModal({ open, onClose, onSubmit, sites, cameras, sellPrice }) {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [csAccepted, setCsAccepted] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Body scroll lock
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Reset fields when modal opens fresh
  useEffect(() => {
    if (open) {
      setError("");
      setSubmitted(false);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const n = name.trim();
    const em = email.trim();
    if (!n) { setError("Please enter your name."); return; }
    if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) { setError("Please enter a valid email address."); return; }
    if (!csAccepted) { setError("Please provide your consent to continue."); return; }
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/collect-email`, {
        name: n,
        company: company.trim(),
        email: em,
        sites,
        cameras,
        sell_price: sellPrice,
      });
      setSubmitted(true);
      setTimeout(() => { onSubmit(); }, 900);
    } catch (err) {
      // Still unlock — don't block user on transient backend issues
      onSubmit();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fs-modal" data-testid="email-modal-overlay" role="dialog" aria-modal="true">
      <button
        className="fs-modal-close"
        onClick={onClose}
        data-testid="fs-modal-close-btn"
        aria-label="Close modal"
      >
        <X size={18} />
      </button>

      <div className="fs-modal-grid">
        {/* LEFT — value prop */}
        <aside className="fs-modal-left">
          <img src={LOGO_URL} alt="247 Alert" className="fs-modal-logo" />
          <h2 className="fs-modal-left-h">
            Unlock Your<br />Revenue Projection
          </h2>
          <p className="fs-modal-left-p">
            See your full monthly, annual, and multi-year RMR potential based on your deployment.
          </p>
          <ul className="fs-modal-benefits">
            {BENEFITS.map((b) => (
              <li key={b}>
                <span className="fs-benefit-icon"><Check size={12} strokeWidth={3} /></span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <div className="fs-modal-preview">
            <div className="fs-preview-row">
              <span>Projected setup</span>
              <span>{sites} × {cameras} cams · ${sellPrice}/cam</span>
            </div>
            <div className="fs-preview-row fs-preview-row-main">
              <span>Monthly RMR</span>
              <span className="fs-preview-blur">•••••</span>
            </div>
          </div>
        </aside>

        {/* RIGHT — form */}
        <section className="fs-modal-right">
          <div className="fs-form-card" data-testid="email-modal">
            <div className="fs-form-head">
              <div className="fs-form-lock"><Lock size={16} /></div>
              <div>
                <h3 className="fs-form-title">Unlock Your Revenue Projection</h3>
                <p className="fs-form-sub">
                  Enter your email to see your full monthly and annual RMR potential.
                </p>
              </div>
            </div>

            {submitted ? (
              <div className="fs-form-success" data-testid="modal-success">
                <div className="fs-form-success-icon"><Check size={22} strokeWidth={3} /></div>
                <p>Unlocking your results…</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="fs-form" data-testid="email-form">
                <div className="fs-field">
                  <label className="fs-field-label">Name</label>
                  <div className="fs-input-wrap">
                    <User size={14} />
                    <input
                      data-testid="name-input"
                      type="text"
                      placeholder="Your full name"
                      value={name}
                      onChange={(e) => { setName(e.target.value); setError(""); }}
                      autoFocus
                    />
                  </div>
                </div>

                <div className="fs-field">
                  <label className="fs-field-label">Company <span className="fs-field-optional">(optional)</span></label>
                  <div className="fs-input-wrap">
                    <Building2 size={14} />
                    <input
                      data-testid="company-input"
                      type="text"
                      placeholder="Your company"
                      value={company}
                      onChange={(e) => { setCompany(e.target.value); setError(""); }}
                    />
                  </div>
                </div>

                <div className="fs-field">
                  <label className="fs-field-label">Email</label>
                  <div className="fs-input-wrap">
                    <Mail size={14} />
                    <input
                      data-testid="email-input"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(""); }}
                    />
                  </div>
                </div>

                <label className="fs-check" data-testid="cs-label">
                  <input
                    data-testid="cs-checkbox"
                    type="checkbox"
                    checked={csAccepted}
                    onChange={(e) => { setCsAccepted(e.target.checked); setError(""); }}
                  />
                  <span>
                    I agree to receive communications from 247 Alert, including email, phone, and text messages, and understand I can opt out at any time.
                  </span>
                </label>

                {error && (
                  <p className="fs-form-error" data-testid="email-error">{error}</p>
                )}

                <button
                  type="submit"
                  className="fs-form-btn"
                  data-testid="email-submit-btn"
                  disabled={submitting}
                >
                  {submitting ? "Unlocking…" : "View My Results"}
                </button>

                <p className="fs-form-helper">We’ll also send you a copy of your estimate.</p>
                <p className="fs-form-trust">🔒 Your information is private and never sold.</p>
              </form>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────────────────── */
export function CalculatorPage() {
  const [sites, setSites] = useState(5);
  const [cameras, setCameras] = useState(8);
  const [sellPrice, setSellPrice] = useState(110);
  const [sellPriceInput, setSellPriceInput] = useState("110");
  const [unlocked, setUnlocked] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const totalCameras = sites * cameras;
  const monthlyRMR = useMemo(() => totalCameras * sellPrice, [totalCameras, sellPrice]);
  const annualRMR  = monthlyRMR * 12;

  const handleSellPriceChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    setSellPriceInput(raw);
    const num = parseInt(raw, 10);
    if (!isNaN(num) && num > 0) setSellPrice(num);
  };

  const handleUnlockSubmit = () => {
    setUnlocked(true);
    setShowModal(false);
  };

  return (
    <div className="app-root">
      <div className="bg-texture" />

      <FullScreenLeadGateModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleUnlockSubmit}
        sites={sites}
        cameras={cameras}
        sellPrice={sellPrice}
      />

      {/* NAV */}
      <nav className="nav-bar">
        <div className="nav-inner">
          <a href="/" className="nav-logo-img-wrap">
            <img src={LOGO_URL} alt="247 Alert" className="nav-logo-img" />
          </a>
          <a
            href="https://247alert.com/contact-us/"
            target="_blank"
            rel="noreferrer"
            className="nav-cta"
            data-testid="become-dealer-btn"
          >
            Become a Dealer
          </a>
        </div>
      </nav>

      {/* REVENUE SECTION — text left, calculator right */}
      <section className="rev-section">
        <div className="container">
          <div className="rev-grid">
            <RevenueTextBlock />
            <RevenueCalculatorCard
              sites={sites}
              setSites={setSites}
              cameras={cameras}
              setCameras={setCameras}
              sellPriceInput={sellPriceInput}
              handleSellPriceChange={handleSellPriceChange}
              sellPrice={sellPrice}
              totalCameras={totalCameras}
              monthlyRMR={monthlyRMR}
              annualRMR={annualRMR}
              unlocked={unlocked}
              onUnlock={() => setShowModal(true)}
            />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="how-section">
        <div className="container">
          <div className="how-header">
            <span className="panel-label">How It Works</span>
            <h2 className="how-h2">Build Predictable Recurring Revenue</h2>
          </div>
          <div className="how-grid">
            <div className="how-card">
              <div className="how-num">01</div>
              <h3 className="how-title">Install Cameras</h3>
              <p className="how-body">
                Deploy IP cameras at your customer's sites and connect them to the 247 Alert monitoring platform.
              </p>
            </div>
            <div className="how-card">
              <div className="how-num">02</div>
              <h3 className="how-title">Set Your Price</h3>
              <p className="how-body">
                You control your sell price to customers. The difference between what you charge and what you pay is your recurring profit.
              </p>
            </div>
            <div className="how-card">
              <div className="how-num">03</div>
              <h3 className="how-title">Earn RMR</h3>
              <p className="how-body">
                You collect recurring monthly revenue for every camera that remains active — completely predictable income.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container cta-inner">
          <div className="cta-watermark">247</div>
          <h2 className="cta-h2">Ready to Start Earning RMR?</h2>
          <p className="cta-sub">Contact us today and start offering 247 Alert to your clients.</p>
          <a
            href="https://247alert.com/contact-us/"
            target="_blank"
            rel="noreferrer"
            className="cta-btn"
            data-testid="contact-cta-btn"
          >
            Get Started as a Dealer
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="app-footer">
        <div className="container footer-inner">
          <div className="footer-logo-img-wrap">
            <img src={LOGO_URL} alt="247 Alert" className="footer-logo-img" />
          </div>
          <p className="footer-copy">
            &copy; {new Date().getFullYear()} 247 Alert Remote Monitoring Ltd. All rights reserved.
          </p>
          <a href="https://247alert.com" target="_blank" rel="noreferrer" className="footer-link">
            247alert.com
          </a>
        </div>
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<CalculatorPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </BrowserRouter>
  );
}
